import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Panel, Btn, fmt, useOps } from "./ui";
import DigestPanel from "./Digest";
import HealthChecks from "./HealthChecks";

const TYPE_COLOR = { email_error: "text-red-400", webhook_rejected: "text-red-400", photo_reject: "text-amber-300", photo_flagged: "text-amber-300",
  client_error: "text-red-400", health_check: "text-red-400",
  make_poster_error: "text-red-400", process_photo_error: "text-red-400", send_email_error: "text-red-400", backup: "text-sky-300", alert: "text-red-300", bounce: "text-amber-300" };

export default function Health() {
  const { api } = useOps();
  const [d, setD] = useState(null);
  const [filter, setFilter] = useState("");
  const load = useCallback(() => api.get("/health").then((r) => setD(r.data)), [api]);
  useEffect(() => { load(); const id = setInterval(load, 10000); return () => clearInterval(id); }, [load]);
  if (!d) return null;
  const retry = async (id) => { await api.post(`/jobs/${id}/retry`); toast.success("Retry queued"); load(); };
  const types = [...new Set(d.events.map((e) => e.type))];
  const events = d.events.filter((e) => !filter || e.type === filter);
  const b = d.backup;

  return (
    <div className="grid gap-3 lg:grid-cols-3" data-testid="admin-system-health">
      <HealthChecks checks={d.checks} onRan={load} />
      <Panel title="Background jobs" testid="health-jobs">
        <div className="grid grid-cols-4 gap-2 text-center">
          {Object.entries(d.job_counts).map(([k, v]) => (
            <div key={k} data-testid={`health-jobs-${k}`} className="rounded bg-[#1a2332] py-2"><div className="mono text-lg">{v}</div><div className="text-[10px] uppercase text-slate-500">{k}</div></div>
          ))}
        </div>
        <div className="mt-3 space-y-1">
          {d.failed_jobs.map((j) => (
            <div key={j.id} className="flex items-center gap-2 text-[11px]">
              <span className="mono text-red-300">{j.type}</span><span className="truncate text-slate-500" title={j.last_error}>{j.last_error}</span>
              <Btn variant="ghost" className="ml-auto" onClick={() => retry(j.id)} data-testid={`health-retry-${j.id}`}>Retry</Btn>
            </div>
          ))}
        </div>
      </Panel>
      <Panel title="Last backup" testid="health-backup">
        {b ? (
          <div className="text-xs space-y-1">
            <div>Status: <span data-testid="health-backup-status" className={b.ok ? "text-green-400" : "text-red-400"}>{b.ok ? "OK" : "FAILED"}</span></div>
            <div className="text-slate-400">At: {fmt(b.at)}</div>
            {b.key && <div className="mono text-slate-400 break-all">{b.key}</div>}
            {b.error && <div className="text-red-300">{b.error}</div>}
          </div>
        ) : <div data-testid="health-backup-status" className="text-xs text-slate-500">No backup has run yet (runs nightly on the server).</div>}
        <div className="mt-3 text-[11px] text-slate-500">Storage: {d.storage} · Postmark: {d.postmark ? "configured" : "not configured"}</div>
      </Panel>
      <Panel title="Bounces & complaints" testid="health-bounces">
        {d.bounces.length ? d.bounces.map((x) => (
          <div key={x.id} className="text-[11px]"><span className="text-amber-300">{x.type}</span> <span className="text-slate-400">{x.email}</span> <span className="text-slate-600">{fmt(x.received_at)}</span></div>
        )) : <div className="text-xs text-slate-500">None.</div>}
      </Panel>
      <DigestPanel />
      <Panel title="Poster render time (stills under 2 s, Story video under 15 s)" testid="health-poster-render">
        {d.poster_renders?.length ? d.poster_renders.map((s) => (
          <div key={s.size} data-testid={`health-render-${s.size}`} className="flex items-center gap-3 text-[11px]">
            <span className="mono w-12 text-slate-300">{s.size}</span>
            <span>last <b className="mono">{s.last_ms}</b> ms{s.last_kb ? ` · ${s.last_kb} KB` : ""}</span>
            <span className="text-slate-400">avg {s.avg_ms} · max {s.max_ms} ({s.count})</span>
            <span className={s.over_target ? "text-red-400" : "text-green-400"}>{s.over_target ? `${s.over_target} over ${s.target_ms / 1000} s` : "OK"}</span>
          </div>
        )) : <div className="text-xs text-slate-500" data-testid="health-render-none">No posters rendered yet.</div>}
      </Panel>
      <Panel title="Recent events & errors" className="lg:col-span-3" testid="health-events" actions={
        <select data-testid="health-event-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded border border-[#243144] bg-[#1a2332] px-1 py-0.5 text-xs">
          <option value="">all types</option>{types.map((t) => <option key={t}>{t}</option>)}
        </select>}>
        <div className="max-h-[50vh] overflow-auto">
          {events.map((e) => (
            <div key={e.id} className="flex gap-3 border-b border-[#243144] py-1 text-[11px] last:border-0">
              <span className="w-36 shrink-0 text-slate-500">{fmt(e.created_at)}</span>
              <span className={`w-36 shrink-0 mono ${TYPE_COLOR[e.type] || "text-slate-300"}`}>{e.type}</span>
              <span className="text-slate-300 break-all">{e.message}</span>
            </div>
          ))}
          {!events.length && <div className="text-xs text-slate-500">Nothing logged.</div>}
        </div>
      </Panel>
    </div>
  );
}
